import React from "react";
import {
  Button,
  FunctionField,
  Labeled,
  ReferenceField,
  ShowProps,
  TextField,
} from "react-admin";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { GridinRowsWithSpace } from "utils/style";
import CustomDateField from "components/CustomDateField";
import { GetParam } from "utils/tools";
import EditIcon from "@material-ui/icons/Edit";
import CustomBooleanField from "components/CustomBooleanField";
import { Link } from "react-router-dom";
import CustomTooltip from "../../components/CustomTooltip";
import InfoIcon from "@material-ui/icons/Info";

interface ILinkedAcademicYearProp {
  record: any;
}

const useStyles = makeStyles({
  root: {
    textDecoration: "none",
    color: "#3f51b5",
  },
  summaryButton: {
    position: "relative",
    top: 20,
    padding: "8px 16px",
  },
  captionTooltip: {
    marginTop: "5px",
    marginBottom: "5px",
  },
  captionText: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "2px",
  },
  labelWrapper: {
    display: "inline",
  },
  infoIcon: {
    verticalAlign: "middle",
    marginLeft: "5px",
  },
});

const LinkedAcademicYear = ({ record }: ILinkedAcademicYearProp) => {
  const classes = useStyles();

  return (
    <Link
      to={`/academicYears/${record.id}/show?schoolId=${record.schoolId}`}
      rel="noopener noreferrer"
      className={classes.root}
    >
      {record.name}
    </Link>
  );
};

const Summary = (props: ShowProps) => {
  const param = GetParam();
  const classes = useStyles();

  return (
    <>
      <SanitizedGrid container spacing={2}>
        <Grid item xs={12} style={GridinRowsWithSpace}>
          <Typography variant="h6">Academic Year Detail</Typography>
          <Button
            className={classes.summaryButton}
            onClick={() =>
              props.history.push(
                `/ccaAcademicYears/${props.id}?schoolId=${param.get(
                  "schoolId",
                )}`,
              )
            }
            label="Edit"
          >
            <EditIcon />
          </Button>
        </Grid>

        <Grid item xs={4}>
          <Labeled source="Linked Regular Academic Year">
            <ReferenceField
              source="regularAcademicYearId"
              reference="academicYears"
              link={false}
              emptyText="-"
            >
              <FunctionField
                render={(record: any) => <LinkedAcademicYear record={record} />}
              />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid item xs={8}></Grid>

        <Grid item xs={4}>
          <Labeled source="Name">
            <TextField source="name" emptyText="-" />
          </Labeled>
        </Grid>
        <Grid item xs={8}></Grid>

        <Grid item xs={3}>
          <Labeled source="Start Date">
            <CustomDateField source="startDate" emptyText="-" />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled source="End Date">
            <CustomDateField source="endDate" emptyText="-" />
          </Labeled>
        </Grid>
        <Grid item xs={6}></Grid>

        <Grid item xs={3}>
          <Labeled source="Early Registration Start Date">
            <CustomDateField
              source="earlyRegistrationStartDate"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled source="Early Registration End Date">
            <CustomDateField source="earlyRegistrationEndDate" emptyText="-" />
          </Labeled>
        </Grid>
        <Grid item xs={6}></Grid>

        <Grid item xs={3}>
          <Labeled source="Late Registration Start Date">
            <CustomDateField source="lateRegistrationStartDate" emptyText="-" />
          </Labeled>
        </Grid>
        <Grid item xs={9}></Grid>

        <Grid item xs={3}>
          <Labeled source="Allow Late Registration">
            <CustomBooleanField
              source="allowLateRegistration"
              textForTrueValue="Yes"
              textForFalseValue="No"
            />
          </Labeled>
        </Grid>
        <Grid item xs={9}>
          <FunctionField
            addLabel={false}
            render={(record: any) =>
              record?.allowLateRegistration ? (
                <Labeled source="Late Registration Minimum Days Offset (Days)">
                  <div className={classes.labelWrapper}>
                    <TextField source="lateRegMinDaysOffset" emptyText="-" />
                    <CustomTooltip
                      title={
                        <>
                          <Typography variant="caption">
                            If the Late Registration Minimum Days Offset is set
                            to {record.lateRegMinDaysOffset || 14}, it means
                            that when a parent registers after the Late
                            Registration Start Date, they can only register for
                            dates at least {record.lateRegMinDaysOffset || 14}{" "}
                            days from the registration date.
                          </Typography>
                        </>
                      }
                      aria-label="Late Registration Minimum Days Offset"
                      classes={{
                        tooltip: classes.captionTooltip,
                      }}
                      placement="bottom-start"
                      component="span"
                    >
                      <InfoIcon
                        fontSize="small"
                        color="secondary"
                        className={classes.infoIcon}
                      />
                    </CustomTooltip>
                  </div>
                </Labeled>
              ) : (
                <></>
              )
            }
          />
        </Grid>

        <Grid item xs={3}>
          <Labeled source="Refund Rate (%)">
            <TextField source="refundRate" emptyText="-" />
          </Labeled>
        </Grid>
        <Grid item xs={9}></Grid>
      </SanitizedGrid>
    </>
  );
};

export default Summary;
